import { apiClient } from "../client";
import type { LiveStreamingProvider } from "./liveClasses";

/**
 * Astrologer live streams — an astrologer broadcasting on the spot, open to
 * the whole site.
 *
 * Not to be confused with `liveClasses` (scheduled, enrollment-gated, everyone
 * on stage can talk). Here there is exactly one publisher, an unlimited and
 * mostly signed-out audience, and no booking or enrollment anywhere.
 *
 * Two rules from the server shape everything a client does with this module:
 *
 *  - **Watching needs no account.** `list`, `get`, `join`, `heartbeat`,
 *    `leave` and `chat` all work without a session, so the Live shelf and the
 *    player render for a visitor who has never signed in.
 *  - **Speaking does.** `sendMessage` is the one call that requires one; it
 *    answers 401 otherwise. The join payload says so up front —
 *    `can_chat: false` with `requires_login_for_chat: true` — so the UI shows
 *    a sign-in prompt rather than a dead input.
 *
 * The viewer loop is: join once (keep `viewer_key`), heartbeat every
 * `heartbeat_seconds` with that key, leave when the player closes. Heartbeat
 * doubles as the state poll — it reports `is_live`, the audience count and
 * whether chat is still on, so nothing else has to be polled.
 */

export interface LiveStreamAstrologer {
  id: number;
  name: string;
  display_name?: string | null;
  slug?: string | null;
  photo_url?: string | null;
  short_intro?: string | null;
  experience?: number | null;
}

export interface LiveStream {
  id: number;
  title: string;
  description?: string | null;
  status: "live" | "ended" | string;
  is_live: boolean;
  provider: LiveStreamingProvider;
  chat_enabled: boolean;
  started_at: string;
  ended_at?: string | null;
  /** Watching right now (recent heartbeats). Always 0 once ended. */
  viewer_count: number;
  peak_viewers: number;
  total_viewers: number;
  astrologer?: LiveStreamAstrologer | null;
  [key: string]: unknown;
}

/** `{ success, message, data: [...], meta }` — the paginated list envelope. */
export interface LiveStreamListResponse {
  success?: boolean;
  message?: string;
  data: LiveStream[];
  meta?: { current_page: number; last_page: number; per_page: number; total: number };
}

export interface LiveStreamEnvelope {
  success?: boolean;
  message?: string;
  data: LiveStream;
}

/**
 * What the server hands back for a join. One shape for both roles and both
 * providers — branch on `role` for the controls and on `provider` for the SDK.
 */
export interface LiveStreamSession {
  role: "host" | "viewer" | string;
  is_host: boolean;
  /** True only for the host. A viewer never publishes on these streams. */
  can_publish: boolean;
  /** Whether THIS caller may post in the chat right now. */
  can_chat: boolean;
  /** True when a login is the only thing in the way of chatting. */
  requires_login_for_chat?: boolean;
  /** Viewer only — the session handle for heartbeat/leave. Keep it. */
  viewer_key?: string;
  /** How often to call `heartbeat`. Applies to the host too. */
  heartbeat_seconds: number;
  user_name?: string | null;
  token: string;
  expires_at?: string;
  provider: LiveStreamingProvider;
  /** The room. Same value as `room_name`; the name a VideoSDK client wants. */
  meeting_id: string;
  /** The room. Same value as `meeting_id`; the name a LiveKit client wants. */
  room_name: string;
  chat_enabled: boolean;
  /** LiveKit only — the wss:// URL to connect to. */
  server_url?: string;
  /** LiveKit only — identity pinned in the token (host: user-<id>, viewer: v-<key>). */
  identity?: string;
  /** VideoSDK only — the participantId pinned in the token. */
  participant_id?: string;
  live_stream: LiveStream;
  [key: string]: unknown;
}

export interface LiveStreamSessionResponse {
  success?: boolean;
  message?: string;
  data: LiveStreamSession;
}

export interface LiveStreamHeartbeat {
  status: "live" | "ended" | string;
  /** Tear the player down when this turns false. */
  is_live: boolean;
  viewer_count: number;
  /** Can change mid-stream — an admin may switch chat off. */
  chat_enabled: boolean;
}

export interface LiveStreamChatMessage {
  id: number;
  user_id: number;
  user_name: string;
  message: string;
  created_at: string;
}

export interface LiveStreamChatPage {
  chat_enabled: boolean;
  can_chat: boolean;
  requires_login_for_chat: boolean;
  messages: LiveStreamChatMessage[];
}

/* ── Presentation helpers ───────────────────────────────────────────────────
   Pure functions, no React, kept next to the endpoints (like
   parseLiveClassTime above) because the live sections on BOTH home pages -
   the jyotish app's and the website's - render the same numbers. Each app
   writes its own markup to match its neighbours; only the arithmetic and the
   envelope-digging are shared, so the two can never disagree on what "3
   watching" or "on for 12 min" means. */

/** The stream rows out of whatever envelope the list came back in. */
export function liveStreamRows(res: unknown): LiveStream[] {
  const data = (res as { data?: unknown } | null)?.data;
  if (Array.isArray(data)) return data as LiveStream[];
  const nested = (data as { data?: unknown } | null)?.data;
  if (Array.isArray(nested)) return nested as LiveStream[];
  return [];
}

/** "3 watching" / "1.2k watching" - the label under a live badge. */
export function liveStreamViewerLabel(count: unknown): string {
  const n = Number(count) || 0;
  if (n < 1000) return `${n} watching`;
  return `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}k watching`;
}

/** How long a stream has been running, or ran for. */
export function liveStreamElapsed(startedAt?: string | null, endedAt?: string | null): string {
  const start = new Date(String(startedAt)).getTime();
  if (!Number.isFinite(start)) return "";
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const minutes = Math.max(0, Math.round((end - start) / 60000));
  if (minutes < 1) return "just started";
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** The astrologer's public name, however the payload spells it. */
export function liveStreamAstrologerName(stream?: LiveStream | null): string {
  return stream?.astrologer?.display_name || stream?.astrologer?.name || "Astrologer";
}

export interface GoLivePayload {
  title: string;
  description?: string;
  /** Start with the chat closed by passing false. */
  chat_enabled?: boolean;
}

export const liveStreams = {
  /** What is on air. Tokenless — the public "Live now" shelf. */
  list: (params: { status?: "live" | "ended"; astrologer_id?: number | string; page?: number; per_page?: number } = {}) =>
    apiClient.get<LiveStreamListResponse>("/live-streams", { params: { ...params } }),

  /** One stream's public card. Tokenless; an ended stream still reads, so a
   *  shared link can explain itself. */
  get: (streamId: number | string) => apiClient.get<LiveStreamEnvelope>(`/live-streams/${streamId}`),

  /**
   * Go live now (astrologer only — KYC approved and active). Returns a
   * publisher session.
   *
   * Calling it while already live is **not** an error: the server answers 200
   * with a fresh host token for the running stream, which is what to do after
   * a crash or a reload rather than starting a second broadcast.
   */
  goLive: (payload: GoLivePayload) => apiClient.post<LiveStreamSessionResponse>("/live-streams", payload),

  /** The signed-in astrologer's own broadcasts, newest first (running one
   *  included — this is how an app finds the stream it should resume). */
  mine: (params: { page?: number; per_page?: number } = {}) =>
    apiClient.get<LiveStreamListResponse>("/live-streams/mine", { params: { ...params } }),

  /**
   * Get what you need to connect. Tokenless for viewers; send the host's
   * session and the server answers with a publisher payload instead.
   *
   * A POST because it records a watch session — that is what the audience
   * count is built from. Pass a `viewerKey` you already hold when
   * reconnecting, so the same session is revived rather than another counted.
   */
  join: (streamId: number | string, viewerKey?: string) =>
    apiClient.post<LiveStreamSessionResponse>(
      `/live-streams/${streamId}/join`,
      viewerKey ? { viewer_key: viewerKey } : {},
    ),

  /** "Still here" + the stream's current state. Viewers send their
   *  `viewerKey`; the host sends none (that is what keeps a broadcast with no
   *  audience from being reaped). */
  heartbeat: (streamId: number | string, viewerKey?: string) =>
    apiClient.post<{ success?: boolean; data: LiveStreamHeartbeat }>(
      `/live-streams/${streamId}/heartbeat`,
      viewerKey ? { viewer_key: viewerKey } : {},
    ),

  /** Courtesy call when the player closes so the count drops at once. */
  leave: (streamId: number | string, viewerKey?: string) =>
    apiClient.post<{ success?: boolean; data: { viewer_count: number } }>(
      `/live-streams/${streamId}/leave`,
      viewerKey ? { viewer_key: viewerKey } : {},
    ),

  /** End the broadcast (the astrologer's own, or an admin's force-end).
   *  Idempotent. */
  end: (streamId: number | string, reason?: string) =>
    apiClient.post<LiveStreamEnvelope>(`/live-streams/${streamId}/end`, reason ? { reason } : {}),

  /** Read the chat — tokenless. Poll with `after_id` set to the highest id you
   *  hold; omit it on first load to get the most recent page. */
  chat: (streamId: number | string, params: { after_id?: number; limit?: number } = {}) =>
    apiClient.get<{ success?: boolean; data: LiveStreamChatPage }>(`/live-streams/${streamId}/chat`, {
      params: { ...params },
    }),

  /** Post a message. **Requires a session** — 401 otherwise, which is the
   *  "no interaction without signing in" rule. Max one message per 1.5s. */
  sendMessage: (streamId: number | string, message: string) =>
    apiClient.post<{ success?: boolean; data: LiveStreamChatMessage }>(`/live-streams/${streamId}/chat`, { message }),
};
